import { Fragment, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { useMe, useProfile } from "../../api/hooks";
import { cn } from "../../lib/utils";
import { Avatar } from "../Avatar";
import { CoachAvatar } from "./CoachAvatar";
import { AnchoredArtifacts, ArtifactList } from "./artifacts/ArtifactRenderer";
import { TOKENS } from "../ui/dashboard-primitives";
import { CREAM } from "./tokens";
import { buildRuns } from "./anchoring";
import type { Message, ProposalAnswer } from "./types";
import { OfferControls } from "./OfferControls";
import { OpenerWriteFailed, ProposalControls, VerdictWhy } from "./VerdictOpener";
import { COACH_COPY } from "../../copy/coach";

/**
 * One turn in the coach chat: a 36px avatar on the speaker's side and a bubble
 * on the other.
 *
 * Coach v3 gives *both* roles a bubble and an avatar. The notched corner is
 * which side the turn belongs to — `6px 20px 20px 20px` points left at the
 * coach's avatar, `20px 6px 20px 20px` points right at the user's. Do not
 * "tidy" either into a uniform radius.
 *
 * The coach's bubble holds prose *and* artifact cards in one 12px column: V3
 * turns the assistant column into a real surface and the cards into inset
 * wells within it, and a well outside its container is just a card again.
 */
export function MessageBubble(props: {
  message: Message;
  /**
   * "Got it" under the bubble: the opener's trigger, dismissed (#269,
   * PC-I7). Given by the shell only while the opener is the whole
   * transcript. Interface chrome, so English, whatever the coach language.
   * Resolves false when the server did not take it (PC-I54).
   */
  onDismiss?: () => Promise<unknown> | void;
  /**
   * An answer to the lighter session a verdict's line offers (#270), given
   * by the shell on the same terms as `onDismiss`.
   */
  onAnswer?: (answer: ProposalAnswer) => Promise<unknown>;
  /**
   * An answer to what a trigger's line offers (PC-I71, PC-I75), given by the
   * shell on the same terms. Every kind of offer is drawn by the one
   * `OfferControls` (#318): an exercise to add, a plateau's rep range or
   * deload, and the injured area's ask.
   */
  onAnswerOffer?: (answer: string) => Promise<unknown>;
}) {
  const { message, onDismiss, onAnswer, onAnswerOffer } = props;
  // What the line offers, drawn by the one offer renderer whatever the kind:
  // a kind this client does not know draws "Got it" alone.
  const offer = onAnswerOffer && message.openerOffer ? message.openerOffer : null;
  const [dismissing, setDismissing] = useState(false);
  // "Got it" did not reach the server, and the note under it says so (PC-I54).
  const [dismissFailed, setDismissFailed] = useState(false);
  const isUser = message.role === "user";
  const showThinking = message.role === "assistant" && message.pending === true;
  // Cached by TanStack Query under ['profile']/['me'] -- one message bubble
  // calling these is not one request per bubble, it's a cache read after the
  // first.
  const { data: profile } = useProfile();
  const { data: me } = useMe();
  const { runs, trailing } = useMemo(
    () => buildRuns(message.content, message.artifacts),
    [message.content, message.artifacts],
  );

  return (
    <li className={cn("flex gap-3", isUser && "justify-end")}>
      {!isUser && <CoachAvatar size={36} className="mt-0.5" />}

      {isUser ? (
        <div
          className="max-w-[80%] px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap wrap-break-word"
          style={{
            background: TOKENS.amber,
            color: "#241c17",
            borderRadius: "20px 6px 20px 20px",
            fontFamily: "var(--font-body)",
          }}
        >
          {message.content}
        </div>
      ) : (
        // A verdict's bubble takes its full width from the start, so opening
        // its readout only grows it downward (PC-I58): content-sized, "Why"
        // widened it by about 128px and the card inside reflowed.
        <div
          className={cn(
            "flex min-w-0 max-w-[85%] flex-col items-start gap-1.5",
            message.openerVerdict && "w-full",
          )}
        >
        <div
          className={cn("flex min-w-0 max-w-full flex-col", message.openerVerdict && "w-full")}
          style={{
            gap: "12px",
            borderRadius: "6px 20px 20px 20px",
            padding: "14px 18px 16px",
            background: TOKENS.creamHairline,
            border: "1px solid " + TOKENS.creamBorder,
            boxShadow: "0 24px 48px -20px rgba(20, 14, 12, 0.55)",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {showThinking ? (
              <motion.div
                key="thinking"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{
                  opacity: 0,
                  filter: "blur(2px)",
                  transition: {
                    duration: 0.14,
                    ease: [0.23, 1, 0.32, 1],
                  },
                }}
                transition={{
                  duration: 0.16,
                  ease: [0.23, 1, 0.32, 1],
                }}
                className="flex gap-1.5 py-1"
                role="status"
                aria-label="Coach is thinking"
              >
                {/* The words, not just the label: a live region announces what
                    it contains, and the dots below are aria-hidden. Under
                    prefers-reduced-motion they also stop moving (fonts.css
                    names `coachDotPulse`), so this sentence is the whole of
                    the wait then (#221). Same shape as `PanelSkeleton`. */}
                <span className="sr-only">Coach is thinking</span>
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    aria-hidden
                    className="block w-1.5 h-1.5 rounded-full"
                    style={{
                      background: CREAM,
                      animation: "coachDotPulse 1.4s ease-out infinite",
                      animationDelay: `${i * 0.18}s`,
                    }}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 4, filter: "blur(2px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.26,
                  ease: [0.23, 1, 0.32, 1],
                }}
                className="flex min-w-0 flex-col"
                style={{ color: CREAM, gap: 12 }}
              >
                {/*
                  Runs of prose with the cards anchored to them in between, so a
                  card sits under the sentence it illustrates rather than in a
                  stack at the bottom (Spec K Task 5). With nothing anchored
                  this is exactly one run and renders as it always did — the
                  12px gap has no second child to act on.

                  The prose classes are per-run rather than on this column,
                  because `prose` zeroes its first child's top margin and each
                  run should start flush against the 12px gap above it.

                  `wrap-break-word` lets a word with no space in it (a
                  template name, a pasted URL) break at the bubble's edge
                  instead of running past it and being clipped (#310). Not
                  `wrap-anywhere`, though the add-offer pill needs it (see
                  OfferControls.tsx, a shrink-to-fit flex item): this
                  run is a stretched, definite-width block, so `break-word`
                  is enough, and `anywhere` would also shrink the min-content
                  width the coach's tables size from, so they would stop
                  scrolling and squeeze their columns.
                */}
                {runs.map((run, index) => (
                  <Fragment key={index}>
                    <div className="text-[15px] leading-relaxed wrap-break-word prose prose-sm prose-coach max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                        {run.text}
                      </ReactMarkdown>
                    </div>
                    <AnchoredArtifacts artifacts={run.cards} />
                  </Fragment>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* The verdict's reasons, one tap away under its line (#270,
              PC-I40): inside the bubble, above the session card. */}
          {message.openerVerdict && !showThinking && (
            <VerdictWhy verdict={message.openerVerdict} />
          )}

          {/*
            Inside the bubble, not beside it. V3's artifact card is an inset
            well on the coach's own surface, so it is one more child of this
            12px column — prose→card and card→card spacing come out identical
            and the nesting is invisible.

            Gated on `pending`: while the typing dots are up the bubble shows
            the dots and nothing else. A card that arrives before the reply's
            `done` comes from a tool round and is provisional (an error
            discards it with the reply, `contracts/coach-stream.json`), so it
            is not drawn ahead of the prose (S24, #449). The stream reader
            holds such a card until `done`, so this is the second line of the
            same rule, the one a pending bubble keeps whatever it is handed.
            ArtifactList returns null when there is nothing to show, so a
            message without artifacts renders as a plain prose bubble.

            Only the cards with nowhere to go: no anchor quote, one that could
            not be resolved, or an index past the end of this reply. The
            anchored ones render inside the prose column above, which the
            thinking state already leaves out. A card is never dropped for
            lacking a position — it lands here, which is where every card was
            before Spec K Task 5.
          */}
          {!showThinking && <ArtifactList artifacts={trailing} />}
        </div>

        {/*
          Quiet, under the bubble rather than inside it: an x floating in a
          chat bubble reads like deleting a message (PC-I7). Text, not an icon,
          so the words say what it does. The negative margin lines the text up
          with the bubble's edge while the padding keeps a real target.
        */}
        {/* A verdict's line carries its proposal's controls, which hold its
            own "Got it" where one belongs (PC-I41). */}
        {onDismiss && !showThinking && message.openerVerdict && onAnswer && (
          <ProposalControls
            proposal={message.openerProposal}
            onAnswer={onAnswer}
            onDismiss={onDismiss}
          />
        )}
        {/* An offer carries its own controls, and its decline is the dismissal
            (PC-I75), with its own "Got it" where one belongs (PC-I71). */}
        {onDismiss && !showThinking && !message.openerVerdict && offer && onAnswerOffer && (
          <OfferControls offer={offer} onAnswer={onAnswerOffer} onDismiss={onDismiss} />
        )}
        {onDismiss && !showThinking && !message.openerVerdict && !offer && (
          <button
            type="button"
            disabled={dismissing}
            onClick={async () => {
              setDismissFailed(false);
              setDismissing(true);
              try {
                if ((await onDismiss()) === false) setDismissFailed(true);
              } finally {
                setDismissing(false);
              }
            }}
            className="-ml-2 rounded-lg px-2 py-1.5 text-[13px] text-(--text-cream-soft) transition-[color,transform] duration-150 ease-out hover:text-(--text-cream) active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-(--accent-amber-emphasis) disabled:opacity-60"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {COACH_COPY.openerGotIt}
          </button>
        )}
        {dismissFailed && !message.openerVerdict && !offer && <OpenerWriteFailed />}
        </div>
      )}

      {/* `userId={me?.user_id ?? 0}` keyed the avatar blob on 0, a SHARED key
          across every account on this browser -- the exact hole
          `['avatar', userId]` was chosen to close (api/hooks.ts). The whole
          span is withheld until `me` resolves rather than falling back to 0.
          Unreachable in practice: RequireFirstRun renders null until ['me']
          has resolved, and it gates every page in the shell. */}
      {isUser && me?.user_id != null && (
        <span aria-hidden="true" className="mt-0.5 shrink-0">
          <Avatar
            first={profile?.first_name}
            last={profile?.last_name}
            userId={me.user_id}
            size={36}
          />
        </span>
      )}
    </li>
  );
}
